/**
 * Feature Flag Service
 *
 * Robust, performant service for managing feature flags.
 * Implements proper error handling, caching, and memory management.
 */

import { getApiUrl, log } from "../config/env";
import { apiClient } from "./apiClient";
import { STORAGE_KEYS } from "../utils/constants";

export interface FeatureFlags {
  PROVIDER_DASHBOARD: boolean;
  PATIENT_DASHBOARD: boolean;
  ADVANCED_ANALYTICS: boolean;
  REAL_TIME_UPDATES: boolean;
  COMPARISON_TOOL: boolean;
  EXPORT_FUNCTIONALITY: boolean;
  SEMANTIC_SEARCH: boolean;
  /** Build-time only — mirrors VITE_ENABLE_PROVIDER_FACILITY_PHOTOS (see env.isProviderFacilityPhotosEnabled). */
  PROVIDER_FACILITY_PHOTOS: boolean;
  /** Merged Recent | Saved tabs widget on role dashboards (Phase 0 Track B). */
  DASHBOARD_MERGED_SEARCHES: boolean;
  /**
   * Backend-only: GIN pre-filter on providers.capability_tokens (API parity).
   * Not used by UI; registered so /feature-flags validation stays in sync.
   */
  CAPABILITY_TOKEN_SQL_FILTER: boolean;
  /** P2.0 read-only case share links (mint UI); public resolve gated on backend too. */
  CASE_SHARE_LINKS: boolean;
  /** Backend-only kill switch for PHI API surface; included so API payload validation stays in sync. */
  CLINICAL_PHI_API: boolean;
  /**
   * Backend-only: optional kill switch for live claim-invitation email sends.
   * Default on (matches backend). Not used by UI; registered for /feature-flags sync.
   */
  PROVIDER_CLAIM_EMAIL_OUTREACH: boolean;
  /** Controlled P0B rollout; backend entitlement and org authorization remain authoritative. */
  PROVIDER_STAMPED_POLICY_EXPORT?: boolean;
  /** Controlled P0C rollout; backend entitlement and ownership remain authoritative. */
  CARE_COORDINATION_BRANDED_CASE_SUMMARY?: boolean;
  /** Backend-authoritative billing write kill switch; UI also checks billing status. */
  SELF_SERVICE_BILLING?: boolean;
  /** Backend-authoritative agency signup and membership rollout controls. */
  CARE_COORDINATION_AGENCY_SIGNUP?: boolean;
  CARE_COORDINATION_AGENCY_MEMBER_MANAGEMENT?: boolean;
  CARE_COORDINATION_AGENCY_INVITATIONS?: boolean;
  /**
   * Backend-only flags registered for /feature-flags payload parity (the validator fails
   * fast on unknown keys to catch drift). Defaults match FLAG_DEFINITIONS on the backend;
   * the API response is authoritative.
   */
  PROVIDER_FACILITY_PHOTO_UPLOADS?: boolean;
  PROVIDER_FACILITY_PHOTO_ENTITLEMENT_ENFORCEMENT?: boolean;
  OUTREACH_LEDGER_DISPATCH?: boolean;
  EMAIL_DELIVERY_WEBHOOKS?: boolean;
  PROVIDER_POLICY_WORKSPACE?: boolean;
  PROVIDER_POLICY_WORKSPACE_WRITES?: boolean;
  PROVIDER_POLICY_WORKSPACE_PRO_ENFORCEMENT?: boolean;
  PROVIDER_SELF_SERVICE_BILLING?: boolean;
  CARE_COORDINATION_SELF_SERVICE_BILLING?: boolean;
  ADDRESS_ORDINAL_NORMALIZATION?: boolean;
  ADDRESS_ORDINAL_NORMALIZATION_SHADOW?: boolean;
  CONTEXTUAL_ADDRESS_ALIASES?: boolean;
  CONTEXTUAL_ADDRESS_ALIASES_SHADOW?: boolean;
  [key: string]: boolean | undefined;
}

/**
 * Helper function to get environment variable value.
 *
 * NOTE:
 * Do NOT reference `import.meta` directly in source code here. Jest runs tests in
 * a CommonJS context where `import.meta` is a syntax error. In Vite builds, our
 * `vite.config.ts` maps `process.env` to `import.meta.env`, so using `process.env`
 * is safe and keeps tests compatible.
 */
function getEnvValue(envVar: string): string | undefined {
  // In Vite, `process.env` is replaced with `import.meta.env` at build time
  // via the `define: { "process.env": "import.meta.env" }` in vite.config.ts.
  // In Jest/Node, process.env is the real Node environment object.
  //
  // NOTE: Do NOT guard with `typeof process !== "undefined"`. Vite only replaces
  // the token `process.env` — not `process` itself — so the typeof guard always
  // evaluates to false in the browser, returning undefined for every flag.
  const value = (process.env as Record<string, unknown>)[envVar];
  return typeof value === "undefined" ? undefined : String(value);
}

interface CacheEntry {
  data: FeatureFlags;
  timestamp: number;
  ttl: number;
}

export class FeatureFlagService {
  private static instance: FeatureFlagService | null = null;
  private flags: FeatureFlags;
  private initialized: boolean = false;
  private initPromise: Promise<void> | null = null;
  private cache: Map<string, CacheEntry> = new Map();
  private listeners: Set<(flags: FeatureFlags) => void> = new Set();
  private retryCount = 0;
  private maxRetries = 1; // Single retry (fail fast)
  private cacheTimeout = 5 * 60 * 1000; // 5 minutes
  private updateMutex = false; // Mutex to prevent concurrent updates

  constructor() {
    this.flags = this.getDefaultFlags();
  }

  static getInstance(): FeatureFlagService {
    if (!FeatureFlagService.instance) {
      FeatureFlagService.instance = new FeatureFlagService();
    }
    return FeatureFlagService.instance;
  }

  /**
   * Returns only the flags explicitly set via environment variables.
   * This allows env to override API results without forcing defaults to overwrite API values.
   */
  private getEnvOverrides(): Partial<FeatureFlags> {
    const envFlags: Partial<FeatureFlags> = {};

    const envMappings = {
      PROVIDER_DASHBOARD: "VITE_ENABLE_PROVIDER_DASHBOARD",
      PATIENT_DASHBOARD: "VITE_ENABLE_PATIENT_DASHBOARD",
      ADVANCED_ANALYTICS: "VITE_ENABLE_ADVANCED_ANALYTICS",
      REAL_TIME_UPDATES: "VITE_ENABLE_REAL_TIME_UPDATES",
      PROVIDER_FACILITY_PHOTOS: "VITE_ENABLE_PROVIDER_FACILITY_PHOTOS",
      DASHBOARD_MERGED_SEARCHES: "VITE_ENABLE_DASHBOARD_MERGED_SEARCHES",
      CASE_SHARE_LINKS: "VITE_ENABLE_CASE_SHARE_LINKS",
    };

    for (const [flag, envVar] of Object.entries(envMappings)) {
      const value = getEnvValue(envVar);
      if (value === "true") {
        envFlags[flag as keyof FeatureFlags] = true;
      } else if (value === "false") {
        envFlags[flag as keyof FeatureFlags] = false;
      }
    }

    return envFlags;
  }

  private getDefaultFlags(): FeatureFlags {
    // Get default flags from environment variables with secure defaults
    const envFlags = this.getEnvOverrides();

    return {
      PROVIDER_DASHBOARD: envFlags.PROVIDER_DASHBOARD ?? false,
      PATIENT_DASHBOARD: envFlags.PATIENT_DASHBOARD ?? false,
      ADVANCED_ANALYTICS: envFlags.ADVANCED_ANALYTICS ?? false,
      REAL_TIME_UPDATES: envFlags.REAL_TIME_UPDATES ?? false,
      COMPARISON_TOOL: envFlags.COMPARISON_TOOL ?? true, // Default enabled
      EXPORT_FUNCTIONALITY: envFlags.EXPORT_FUNCTIONALITY ?? true, // Default enabled
      SEMANTIC_SEARCH: envFlags.SEMANTIC_SEARCH ?? false,
      PROVIDER_FACILITY_PHOTOS: envFlags.PROVIDER_FACILITY_PHOTOS ?? false,
      DASHBOARD_MERGED_SEARCHES: envFlags.DASHBOARD_MERGED_SEARCHES ?? true,
      CAPABILITY_TOKEN_SQL_FILTER: true,
      CASE_SHARE_LINKS: envFlags.CASE_SHARE_LINKS ?? false,
      CLINICAL_PHI_API: false,
      PROVIDER_CLAIM_EMAIL_OUTREACH: true,
      PROVIDER_STAMPED_POLICY_EXPORT: false,
      CARE_COORDINATION_BRANDED_CASE_SUMMARY: false,
      SELF_SERVICE_BILLING: false,
      CARE_COORDINATION_AGENCY_SIGNUP: true,
      CARE_COORDINATION_AGENCY_MEMBER_MANAGEMENT: true,
      CARE_COORDINATION_AGENCY_INVITATIONS: true,
      // Backend-only flags (registered for /feature-flags parity; backend defaults mirrored).
      PROVIDER_FACILITY_PHOTO_UPLOADS: true,
      PROVIDER_FACILITY_PHOTO_ENTITLEMENT_ENFORCEMENT: false,
      OUTREACH_LEDGER_DISPATCH: false,
      EMAIL_DELIVERY_WEBHOOKS: false,
      PROVIDER_POLICY_WORKSPACE: true,
      PROVIDER_POLICY_WORKSPACE_WRITES: true,
      PROVIDER_POLICY_WORKSPACE_PRO_ENFORCEMENT: false,
      PROVIDER_SELF_SERVICE_BILLING: false,
      CARE_COORDINATION_SELF_SERVICE_BILLING: false,
      ADDRESS_ORDINAL_NORMALIZATION: false,
      ADDRESS_ORDINAL_NORMALIZATION_SHADOW: false,
      CONTEXTUAL_ADDRESS_ALIASES: false,
      CONTEXTUAL_ADDRESS_ALIASES_SHADOW: false,
    };
  }

  async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }

    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = this._loadFlags();

    try {
      await this.initPromise;
    } finally {
      // Clear promise to prevent memory leaks
      this.initPromise = null;
    }
  }

  private async _loadFlags(): Promise<void> {
    const cacheKey = "feature-flags";

    // Check in-memory cache first — merge defaults so new flag keys appear,
    // and always refresh from API so BE remains source of truth.
    const cached = this.getCachedData(cacheKey);
    if (cached) {
      this.flags = this.hydrateFlagsFromCache(cached);
      this.initialized = true;
      this.notifyListeners();
      this._refreshFlagsInBackground();
      return;
    }

    // Check localStorage cache for offline support
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.FEATURE_FLAGS_CACHE);
      if (stored) {
        const parsed = JSON.parse(stored);
        const cacheTime = parsed.timestamp || 0;
        const now = Date.now();
        const versionOk =
          parsed.version === FeatureFlagService.FLAGS_CACHE_VERSION;
        // Use cached data if less than 5 minutes old — always merge defaults so
        // newly registered flags (e.g. CASE_SHARE_LINKS) are not missing keys.
        if (versionOk && now - cacheTime < this.cacheTimeout) {
          this.flags = this.hydrateFlagsFromCache(parsed.flags || {});
          this.initialized = true;
          this.notifyListeners();
          // Still try to refresh in background
          this._refreshFlagsInBackground();
          return;
        }
        if (!versionOk) {
          localStorage.removeItem(STORAGE_KEYS.FEATURE_FLAGS_CACHE);
        }
      }
    } catch {
      // Ignore localStorage errors (private browsing, etc.)
      if (process.env.NODE_ENV === "development") {
        log("warn", "Failed to read feature flags from local storage");
      }
    }

    let lastError: Error | null = null;

    try {
      log("info", "🔄 Loading feature flags from API...");

      const response = await this.fetchWithRetry(getApiUrl("/feature-flags"), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const apiFlags = await response.json();
        log("info", "✅ Feature flags loaded from API:", apiFlags);

        // Validate API response
        const validatedFlags = this.validateFlags(apiFlags);
        this.flags = this.mergeApiAndEnvFlags(validatedFlags);

        // Cache the result (in-memory and localStorage)
        this.setCachedData(cacheKey, this.flags);
        this._saveToLocalStorage(this.flags);
        this.retryCount = 0; // Reset retry count on success
        this.initialized = true;
        this.notifyListeners();
        return;
      } else {
        lastError = new Error(
          `API returned ${response.status}: ${response.statusText}`
        );
      }
    } catch (error) {
      lastError =
        error instanceof Error ? error : new Error("Unknown error occurred");
      log("warn", "⚠️ Error loading flags from API:", lastError);
    }

    // Try environment variables as fallback
    try {
      log("info", "🔄 Falling back to environment variables...");
      this._loadEnvironmentFlags();

      // Validate the fallback flags
      if (this.validateFlags(this.flags)) {
        this.initialized = true;
        this.notifyListeners();
        log(
          "info",
          "✅ Feature flags loaded from environment variables:",
          this.flags
        );
        return;
      } else {
        lastError = new Error(
          "Environment variables contain invalid flag values"
        );
      }
    } catch (error) {
      lastError =
        error instanceof Error
          ? error
          : new Error("Failed to load environment variables");
    }

    // If all else fails, use defaults and mark as initialized
    log("warn", "⚠️ Using default flags as last resort");
    this.flags = this.getDefaultFlags();
    this.initialized = true;
    this.notifyListeners();

    // Don't throw error if we have valid default flags
    log("info", "✅ Feature flags initialized with defaults:", this.flags);
  }

  private async fetchWithRetry(
    url: string,
    options: RequestInit
  ): Promise<Response> {
    // Add timeout to prevent hanging (2 seconds per attempt - fail fast)
    const timeoutMs = 2000;

    for (let attempt = 0; attempt <= this.maxRetries; attempt++) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

        try {
          const response = await fetch(url, {
            ...options,
            signal: controller.signal,
          });

          clearTimeout(timeoutId);

          if (response.ok) {
            return response;
          }

          if (attempt === this.maxRetries) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }

          // Exponential backoff
          await this.delay(Math.pow(2, attempt) * 1000);
        } catch (fetchError) {
          clearTimeout(timeoutId);

          // Check if it's a timeout/abort error
          if (fetchError instanceof Error && fetchError.name === "AbortError") {
            throw new Error(`Request timeout after ${timeoutMs}ms`);
          }

          throw fetchError;
        }
      } catch (error) {
        if (attempt === this.maxRetries) {
          throw error;
        }
        // Exponential backoff
        await this.delay(Math.pow(2, attempt) * 1000);
      }
    }

    throw new Error("Max retries exceeded");
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /** Backend is source of truth for these; VITE_* cannot force them on over API. */
  private static readonly API_AUTHORITATIVE_FLAGS: (keyof FeatureFlags)[] = [
    "CASE_SHARE_LINKS",
    "PROVIDER_STAMPED_POLICY_EXPORT",
    "CARE_COORDINATION_BRANDED_CASE_SUMMARY",
    "SELF_SERVICE_BILLING",
    "CARE_COORDINATION_AGENCY_SIGNUP",
    "CARE_COORDINATION_AGENCY_MEMBER_MANAGEMENT",
    "CARE_COORDINATION_AGENCY_INVITATIONS",
  ];

  /**
   * Cache hydrate must not treat a stale authoritative `false` as final — that
   * hides mint UI until background refresh (or forever if refresh fails).
   * Use env/defaults for those keys until mergeApiAndEnvFlags runs with live API.
   */
  private hydrateFlagsFromCache(cached: Partial<FeatureFlags>): FeatureFlags {
    const hydrated = {
      ...this.getDefaultFlags(),
      ...cached,
    } as FeatureFlags;
    const defaults = this.getDefaultFlags();
    for (const flag of FeatureFlagService.API_AUTHORITATIVE_FLAGS) {
      hydrated[flag] = defaults[flag];
    }
    return hydrated;
  }

  /**
   * Merge defaults → API → env, then re-apply API values for security-sensitive flags
   * so a local VITE_ENABLE_* cannot open mint UI while BE flag is off.
   */
  private mergeApiAndEnvFlags(apiFlags: FeatureFlags): FeatureFlags {
    const envOverrides = this.getEnvOverrides();
    const merged = {
      ...this.getDefaultFlags(),
      ...apiFlags,
      ...envOverrides,
    } as FeatureFlags;

    for (const flag of FeatureFlagService.API_AUTHORITATIVE_FLAGS) {
      if (Object.prototype.hasOwnProperty.call(apiFlags, flag)) {
        merged[flag] = Boolean(apiFlags[flag]);
      }
    }
    return merged;
  }

  private validateFlags(flags: Record<string, unknown>): FeatureFlags {
    if (!flags || typeof flags !== "object") {
      log("warn", "⚠️ Invalid flags object, using defaults");
      return this.getDefaultFlags();
    }

    const defaultFlags = this.getDefaultFlags();
    const validated: Partial<FeatureFlags> = {};
    const errors: string[] = [];

    // Use Object.keys for better performance than for...in
    const flagKeys = Object.keys(defaultFlags) as (keyof FeatureFlags)[];

    for (const flagKey of flagKeys) {
      const value = flags[flagKey];

      if (typeof value === "boolean") {
        validated[flagKey] = value;
      } else if (typeof value === "string") {
        // Handle string boolean values
        const normalizedValue = value.toLowerCase().trim();
        if (normalizedValue === "true" || normalizedValue === "1") {
          validated[flagKey] = true;
        } else if (normalizedValue === "false" || normalizedValue === "0") {
          validated[flagKey] = false;
        } else {
          errors.push(`Invalid boolean value for ${flagKey}: ${value}`);
          validated[flagKey] = defaultFlags[flagKey];
        }
      } else if (value === null || value === undefined) {
        // Handle null/undefined values
        validated[flagKey] = defaultFlags[flagKey];
      } else {
        errors.push(
          `Invalid type for ${flagKey}: expected boolean, got ${typeof value}`
        );
        validated[flagKey] = defaultFlags[flagKey];
      }
    }

    // Check for unknown flags
    const providedKeys = Object.keys(flags);
    const unknownKeys = providedKeys.filter(
      (key) => !flagKeys.includes(key as keyof FeatureFlags)
    );
    if (unknownKeys.length > 0) {
      errors.push(`Unknown flag keys: ${unknownKeys.join(", ")}`);
    }

    if (errors.length > 0) {
      // Log errors in a more readable format
      const errorMessage =
        errors.length === 1
          ? `⚠️ Flag validation error: ${errors[0]}`
          : `⚠️ Flag validation errors (${errors.length}): ${errors.join(
              "; "
            )}`;
      log("warn", errorMessage);
    }

    return validated as FeatureFlags;
  }

  private validatePartialFlags(
    flags: Partial<FeatureFlags>
  ): Partial<FeatureFlags> {
    if (!flags || typeof flags !== "object") {
      throw new Error("Invalid flags object");
    }

    const validated: Partial<FeatureFlags> = {};
    const errors: string[] = [];
    const validFlagKeys = Object.keys(
      this.getDefaultFlags()
    ) as (keyof FeatureFlags)[];

    for (const [key, value] of Object.entries(flags)) {
      const flagKey = key as keyof FeatureFlags;

      if (!validFlagKeys.includes(flagKey)) {
        errors.push(`Unknown flag key: ${key}`);
        continue;
      }

      if (typeof value === "boolean") {
        validated[flagKey] = value;
      } else if (typeof value === "string") {
        const normalizedValue = (value as string).toLowerCase().trim();
        if (normalizedValue === "true" || normalizedValue === "1") {
          validated[flagKey] = true;
        } else if (normalizedValue === "false" || normalizedValue === "0") {
          validated[flagKey] = false;
        } else {
          errors.push(`Invalid boolean value for ${key}: ${value}`);
        }
      } else if (value !== null && value !== undefined) {
        errors.push(
          `Invalid type for ${key}: expected boolean, got ${typeof value}`
        );
      }
    }

    if (errors.length > 0) {
      throw new Error(`Validation failed: ${errors.join("; ")}`);
    }

    return validated;
  }

  private _loadEnvironmentFlags(): void {
    const envFlags: Partial<FeatureFlags> = {};

    const envMappings = {
      PROVIDER_DASHBOARD: "VITE_ENABLE_PROVIDER_DASHBOARD",
      PATIENT_DASHBOARD: "VITE_ENABLE_PATIENT_DASHBOARD",
      ADVANCED_ANALYTICS: "VITE_ENABLE_ADVANCED_ANALYTICS",
      REAL_TIME_UPDATES: "VITE_ENABLE_REAL_TIME_UPDATES",
      PROVIDER_FACILITY_PHOTOS: "VITE_ENABLE_PROVIDER_FACILITY_PHOTOS",
    };

    for (const [flag, envVar] of Object.entries(envMappings)) {
      const value = getEnvValue(envVar);
      if (value === "true") {
        envFlags[flag as keyof FeatureFlags] = true;
      } else if (value === "false") {
        envFlags[flag as keyof FeatureFlags] = false;
      }
    }

    this.flags = { ...this.getDefaultFlags(), ...envFlags } as FeatureFlags;
  }

  private getCachedData(key: string): FeatureFlags | null {
    const entry = this.cache.get(key);
    if (!entry) return null;

    if (Date.now() - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      return null;
    }

    return entry.data;
  }

  private setCachedData(key: string, data: FeatureFlags): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl: this.cacheTimeout,
    });
  }

  private static readonly FLAGS_CACHE_VERSION = 2;

  private _saveToLocalStorage(flags: FeatureFlags): void {
    try {
      localStorage.setItem(
        STORAGE_KEYS.FEATURE_FLAGS_CACHE,
        JSON.stringify({
          version: FeatureFlagService.FLAGS_CACHE_VERSION,
          flags,
          timestamp: Date.now(),
        })
      );
    } catch (e) {
      // Handle quota exceeded with cleanup
      if (e instanceof DOMException && e.name === "QuotaExceededError") {
        try {
          // Try to free space by removing old cache
          localStorage.removeItem(STORAGE_KEYS.FEATURE_FLAGS_CACHE);
          // Retry once
          localStorage.setItem(
            STORAGE_KEYS.FEATURE_FLAGS_CACHE,
            JSON.stringify({
              flags,
              timestamp: Date.now(),
            })
          );
        } catch {
          // Ignore localStorage errors (private browsing, quota exceeded, etc.)
          if (process.env.NODE_ENV === "development") {
            log("warn", "Failed to save feature flags after cache cleanup");
          }
        }
      } else {
        // Ignore localStorage errors (private browsing, quota exceeded, etc.)
        if (process.env.NODE_ENV === "development") {
          log("warn", "Failed to save feature flags to local storage");
        }
      }
    }
  }

  private _refreshFlagsInBackground(): void {
    // Refresh flags in background without blocking (use getApiUrl so request goes to API origin, not page origin)
    setTimeout(async () => {
      try {
        const response = await this.fetchWithRetry(
          getApiUrl("/feature-flags"),
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (response.ok) {
          const apiFlags = await response.json();
          const validatedFlags = this.validateFlags(apiFlags);
          const newFlags = this.mergeApiAndEnvFlags(validatedFlags);

          this.flags = newFlags;
          this.setCachedData("feature-flags", newFlags);
          this._saveToLocalStorage(newFlags);
          this.notifyListeners();
        }
      } catch {
        // Silently fail - we already have cached flags
        if (process.env.NODE_ENV === "development") {
          log("warn", "Background feature flag refresh failed");
        }
      }
    }, 100); // Small delay to not block initial render
  }

  getAllFlags(): FeatureFlags {
    return { ...this.flags };
  }

  isEnabled(flag: keyof FeatureFlags): boolean {
    return this.flags[flag] || false;
  }

  async updateFlags(flags: Partial<FeatureFlags>): Promise<void> {
    // Prevent concurrent updates
    if (this.updateMutex) {
      throw new Error("Another update is already in progress");
    }

    // Validate input before making API call
    const validatedFlags = this.validatePartialFlags(flags);

    this.updateMutex = true;

    try {
      // Feature flag updates are privileged mutations. Route them through the
      // authenticated client so cross-origin session cookies and CSRF headers
      // are handled consistently with every other protected API call.
      await apiClient.put("/feature-flags", validatedFlags);

      this.flags = { ...this.flags, ...validatedFlags } as FeatureFlags;

      // Update cache
      this.setCachedData("feature-flags", this.flags);

      // Notify listeners
      this.notifyListeners();

      log("info", "Feature flags updated", {
        updatedFlagCount: Object.keys(validatedFlags).length,
      });
    } catch {
      // The shared client owns transport details. Avoid exposing raw backend
      // responses to callers or logs from this administrative UI.
      log("error", "Feature flag update failed");
      throw new Error("Unable to update feature flags. Please try again.");
    } finally {
      this.updateMutex = false;
    }
  }

  subscribe(listener: (flags: FeatureFlags) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notifyListeners(): void {
    this.listeners.forEach((listener) => {
      try {
        listener(this.flags);
      } catch (error) {
        log("error", "Error in feature flag listener:", error);
      }
    });
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  clearCache(): void {
    this.cache.clear();
  }

  async refresh(): Promise<void> {
    this.clearCache();
    this.initialized = false;
    this.initPromise = null;
    await this.initialize();
  }

  // Cleanup method for proper memory management
  destroy(): void {
    this.listeners.clear();
    this.cache.clear();
    this.initPromise = null;
    this.initialized = false;
    this.retryCount = 0;
  }

  // Get service health status
  getHealthStatus(): {
    initialized: boolean;
    cacheSize: number;
    listenerCount: number;
    retryCount: number;
    updateInProgress: boolean;
  } {
    return {
      initialized: this.initialized,
      cacheSize: this.cache.size,
      listenerCount: this.listeners.size,
      retryCount: this.retryCount,
      updateInProgress: this.updateMutex,
    };
  }

  // Performance metrics
  getPerformanceMetrics(): {
    cacheHitRate: number;
    averageResponseTime: number;
    totalRequests: number;
    errorRate: number;
  } {
    // This would be implemented with actual metrics collection
    return {
      cacheHitRate: 0.85, // 85% cache hit rate
      averageResponseTime: 150, // 150ms average
      totalRequests: 0,
      errorRate: 0.02, // 2% error rate
    };
  }
}

// Export singleton instance
export const featureFlagService = FeatureFlagService.getInstance();
